import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, CheckCircle, Truck, ExternalLink } from "lucide-react";
import { trackOrder } from "../../config/api";
import {
  ORDER_FULFILLMENT_PIPELINE,
  getOrderStatusLabel,
  isOrderStatusComplete,
  normalizeOrderStatus,
} from "../../utils/orderStatusUtils";

const STEP_LABELS = {
  pending: "Placed",
  paid: "Paid",
  confirmed: "Confirmed",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
};

const OrderTrackingPanel = ({ order, formatDate }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  useEffect(() => {
    if (!order?.order_token) return;

    let cancelled = false;

    const loadTimeline = async () => {
      setLoadingTimeline(true);
      try {
        const result = await trackOrder(order.order_token);
        if (!cancelled && result.success) {
          setTrackingData(result.data);
        }
      } catch {
        if (!cancelled) setTrackingData(null);
      } finally {
        if (!cancelled) setLoadingTimeline(false);
      }
    };

    loadTimeline();
    return () => {
      cancelled = true;
    };
  }, [order?.order_token]);

  const normalizedStatus = normalizeOrderStatus(order.status);
  const trackingNumber =
    order.tracking_number || trackingData?.trackingNumber;
  const trackingUrl = order.tracking_url || trackingData?.trackingUrl;
  const shippingCompany =
    order.shipping_company || trackingData?.shippingCompany;
  const timeline = trackingData?.timeline || [];

  return (
    <div className="space-y-4">
      {order.order_token && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 p-4">
          <div>
            <p className="text-sm font-medium text-amber-900 font-outfit">
              Live order tracking
            </p>
            <p className="text-xs text-amber-700 font-poppins mt-1">
              Status updates sync with your confirmation email tracking link.
            </p>
          </div>
          <Link
            to={`/track/${order.order_token}`}
            className="inline-flex items-center px-3 py-2 text-sm font-medium bg-black text-white hover:bg-gray-800 transition-colors font-poppins"
          >
            <Truck className="w-4 h-4 mr-2" />
            Open Tracking Page
          </Link>
        </div>
      )}

      <div className="bg-white border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-4 font-outfit">
          Fulfillment Progress
        </h3>
        <div className="flex items-center justify-between gap-1 mb-2">
          {ORDER_FULFILLMENT_PIPELINE.map((step, index) => {
            const complete = isOrderStatusComplete(step, order.status);
            const isCurrent = normalizedStatus === step;

            return (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center flex-1 min-w-0">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      complete
                        ? "bg-green-500 text-white"
                        : isCurrent
                          ? "bg-amber-500 text-white"
                          : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {complete ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <span className="text-xs font-semibold">{index + 1}</span>
                    )}
                  </div>
                  <p
                    className={`text-[10px] sm:text-xs mt-2 text-center font-poppins ${
                      isCurrent ? "font-semibold text-gray-900" : "text-gray-500"
                    }`}
                  >
                    {STEP_LABELS[step]}
                  </p>
                </div>
                {index < ORDER_FULFILLMENT_PIPELINE.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mb-6 ${
                      isOrderStatusComplete(
                        ORDER_FULFILLMENT_PIPELINE[index + 1],
                        order.status,
                      )
                        ? "bg-green-500"
                        : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <p className="text-sm text-gray-600 font-poppins">
          Current status:{" "}
          <span className="font-medium text-gray-900">
            {getOrderStatusLabel(order.status)}
          </span>
        </p>
      </div>

      {trackingNumber && (
        <div className="bg-indigo-50 border border-indigo-200 p-4">
          <h3 className="font-semibold text-indigo-900 mb-3 flex items-center font-outfit">
            <Truck className="w-5 h-5 mr-2" />
            Shipment Tracking
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-poppins">
            {shippingCompany && (
              <div>
                <p className="text-indigo-600">Carrier</p>
                <p className="font-medium text-indigo-900">{shippingCompany}</p>
              </div>
            )}
            <div>
              <p className="text-indigo-600">Tracking Number</p>
              <p className="font-medium text-indigo-900 font-mono">
                {trackingNumber}
              </p>
            </div>
            {order.shipped_at && (
              <div className="sm:col-span-2">
                <p className="text-indigo-600">Shipped On</p>
                <p className="text-indigo-900">{formatDate(order.shipped_at)}</p>
              </div>
            )}
          </div>
          {trackingUrl && (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-3 text-sm px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Track Package
            </a>
          )}
        </div>
      )}

      <div className="bg-gray-50 p-4">
        <h3 className="font-semibold text-gray-900 mb-3 font-outfit">
          Order Timeline
        </h3>
        {loadingTimeline ? (
          <p className="text-sm text-gray-500 font-poppins">Loading updates...</p>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 font-poppins">
                  Order Placed
                </p>
                <p className="text-xs text-gray-500 font-poppins">
                  {formatDate(order.created_at)}
                </p>
              </div>
            </div>

            {(order.payment_status === "completed" ||
              ["paid", "processing", "shipped", "delivered"].includes(
                normalizedStatus,
              )) && (
              <div className="flex gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900 font-poppins">
                    Payment Confirmed
                  </p>
                  <p className="text-xs text-gray-500 font-poppins">
                    {formatDate(order.updated_at || order.created_at)}
                  </p>
                </div>
              </div>
            )}

            {timeline.length > 0
              ? timeline.map((entry, idx) => (
                  <div key={idx} className="flex gap-3">
                    <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900 capitalize font-poppins">
                        {getOrderStatusLabel(entry.new_status)}
                      </p>
                      {entry.notes && (
                        <p className="text-xs text-gray-600 font-poppins">
                          {entry.notes}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 font-poppins">
                        {formatDate(entry.changed_at)}
                      </p>
                    </div>
                  </div>
                ))
              : ["processing", "shipped", "delivered"].includes(
                    normalizedStatus,
                  ) && (
                  <div className="flex gap-3">
                    <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900 font-poppins">
                        {getOrderStatusLabel(order.status)}
                      </p>
                      <p className="text-xs text-gray-500 font-poppins">
                        {formatDate(order.updated_at || order.created_at)}
                      </p>
                    </div>
                  </div>
                )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTrackingPanel;
